import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import portalApi, { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import { PortalAppShell, PageContainer, PageHeader, Card, Avatar, Button, EmptyState, Icon, IC, Spinner } from './PortalWidgets';
import { UploadZone, DocumentList } from './PortalResponseDetail';

interface DocGroup {
  response_id: string;
  sender_org_name: string;
  vendor_name: string;
  documents: { id: string; file_name: string; content_type: string | null; size_bytes: number | null; document_type: string | null; description?: string | null; created_at: string }[];
}

// /questionnaire/portal/documents -- one evidence library per company you answer questionnaires
// for (each requester only ever sees its own library). Upload once here and attach from any
// questionnaire that company sends.
export default function PortalDocuments() {
  const query = useQuery({
    queryKey: ['portal', 'documents'],
    queryFn: async () => (await portalApi.get('/v1/questionnaire-responses/portal/documents')).data.groups as DocGroup[],
  });
  const groups = useMemo(() => query.data ?? [], [query.data]);
  const [search, setSearch] = useState('');
  const [uploadFor, setUploadFor] = useState<string | null>(null);

  const total = groups.reduce((n, g) => n + g.documents.length, 0);
  const q = search.trim().toLowerCase();
  const visible = groups.map(g => ({
    ...g,
    documents: q ? g.documents.filter(d => [d.file_name, d.document_type, d.description].some(v => (v || '').toLowerCase().includes(q))) : g.documents,
  })).filter(g => !q || g.documents.length || g.sender_org_name.toLowerCase().includes(q));

  return (
    <PortalAppShell>
      <PageContainer>
        <PageHeader
          title="Documents"
          subtitle="Your evidence library — SOC 2 reports, policies, certificates. Each company you work with has its own library, and only that company can see it."
        />

        {query.isPending ? (
          <Card><div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div></Card>
        ) : query.isError ? (
          <Card><EmptyState icon={IC.alert} title="Couldn't load your documents" action={<Button variant="secondary" onClick={() => query.refetch()}>Try again</Button>}>
            {apiError(query.error, 'Something went wrong.')}
          </EmptyState></Card>
        ) : groups.length === 0 ? (
          <Card><EmptyState icon={IC.file} title="No libraries yet">
            Once a company sends you a questionnaire, you'll be able to upload supporting documents for them here.
          </EmptyState></Card>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
                <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.faint, display: 'inline-flex' }}><Icon d={IC.search} size={15} /></span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents" className="pw-input"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 34px', fontSize: 13.5, fontFamily: sans, color: T.ink, background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 8, outline: 'none' }} />
              </div>
              <span style={{ fontSize: 13, color: T.muted }}>{total} document{total === 1 ? '' : 's'} across {groups.length} compan{groups.length === 1 ? 'y' : 'ies'}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {visible.map(g => (
                <Card key={`${g.response_id}`} padding={0}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: `1px solid ${T.bdr}`, flexWrap: 'wrap' }}>
                    <Avatar name={g.sender_org_name} size={38} square />
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{g.sender_org_name}</div>
                      <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>For {g.vendor_name} · {g.documents.length} document{g.documents.length === 1 ? '' : 's'}</div>
                    </div>
                    <Button size="sm" variant={uploadFor === g.response_id ? 'secondary' : 'primary'} icon={uploadFor === g.response_id ? IC.close : IC.upload}
                      onClick={() => setUploadFor(uploadFor === g.response_id ? null : g.response_id)}>
                      {uploadFor === g.response_id ? 'Close' : 'Upload'}
                    </Button>
                  </div>
                  <div style={{ padding: 20 }}>
                    {uploadFor === g.response_id && (
                      <div style={{ marginBottom: 18 }}>
                        <UploadZone responseId={g.response_id} onUploaded={() => query.refetch()} />
                      </div>
                    )}
                    <DocumentList responseId={g.response_id} docs={g.documents} loading={false} canDelete onChanged={() => query.refetch()} />
                  </div>
                </Card>
              ))}
              {visible.length === 0 && <Card><EmptyState icon={IC.search} title="No matches">No documents match "{search}".</EmptyState></Card>}
            </div>
          </>
        )}
      </PageContainer>
    </PortalAppShell>
  );
}
